import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

const BASE = "/report-card"

// ── Tabulation (class-wide result sheet) ───────────────────────────
export type TabSubject = {
  courseStructureId: string
  subjectName: string
  subjectCode: string
  totalMarks: number
  showGradeOnly: boolean
  subCategories: { _id: string; description: string; marks: number }[]
}

export type TabCell = {
  description: string
  max: number
  value: number | null
  is_absent: boolean
  is_na: boolean
}

export type TabStudentSubject = {
  courseStructureId: string
  subjectName: string
  subjectCode: string
  totalMarks: number
  effectiveTotal: number
  includeInTotal: boolean
  showGradeOnly: boolean
  cells: TabCell[]
  subjectTotal: number
  percent: number | null
  gradeLetter: string | null
  colorCode: string | null
  isAbsent: boolean
  allNa: boolean
}

export type TabStudent = {
  enrollmentId: string
  grNo: string
  rollNo: string | number
  studentName: string
  gender: string
  subjects: TabStudentSubject[]
  grandTotal: number
  grandMax: number
  percent: number
  gradeLetter: string | null
  colorCode: string | null
  isAbsent: boolean
  failedSubjects: string[]
  result: "Pass" | "Fail"
  position: number
  positionWithSuffix: string
}

export type SchoolInfo = { name: string; code: string; headerText: string; headerImageKey: string; logoKey: string; address: string; phone: string }
export type GradeLegendRow = { grade: string; colorCode: string | null; minPercentage: number; maxPercentage: number; remarks: string }

export type Tabulation = {
  breakdown: { id: string; title: string; term?: string; session?: string }
  school: SchoolInfo
  class: { id: string; name: string }
  section: { id: string; name: string }
  gradeLegend: GradeLegendRow[]
  subjects: TabSubject[]
  students: TabStudent[]
  summary: { appeared: number; passed: number; failed: number }
}

export const getTabulation = async (params: {
  breakdown: string
  class: string
  section: string
}): Promise<{ success: boolean; data: Tabulation }> => {
  const res = await apiClient({ method: "GET", url: appendQueryParams(`${BASE}/tabulation`, params) })
  return res.data
}

// ── Section report cards (picker → batch sheet, mirrors RMS) ───────
export type RcCell = { description: string; max: number; value: number | null; is_absent: boolean; is_na: boolean }
export type RcCourse = {
  courseId: string
  courseName: string
  courseIdentifier: string
  subCategories: { description: string; marks: number }[]
  cells: RcCell[]
  obtainedMarks: number
  totalMarks: number
  percentage: number
  grade: string | null
  passFail: string | null
  colorCode: string | null
  showGradeOnly: boolean
  status: "Graded" | "Absent"
}
export type RcMissingCourse = {
  courseId: string
  courseName: string
  courseIdentifier: string
  subCategories: { description: string; marks: number }[]
  status: "Not Assessed"
}
export type SectionCardStudent = {
  enrollmentId: string
  studentName: string
  grNo: string
  rollNo: string | number
  photo: string | null
  courses: RcCourse[]
  missingCourses: RcMissingCourse[]
  attendance: { daysPresent: number; daysTotal: number }
  blurb: string | null
  summary: {
    overallPercentage: number
    overallGrade: string | null
    overallPassFail: string
    position: number
    positionWithSuffix: string
    promotionStatus: string
    remarks: string
    totalObtained: number
    totalMarks: number
  }
}
export type SectionReportCards = {
  breakdown: { id: string; title: string; term?: string; session?: string }
  school: SchoolInfo
  class: { id: string; name: string }
  section: { id: string; name: string }
  gradeLegend: GradeLegendRow[]
  template: { _id: string; name: string; definition: { canvas?: { width: number; height: number; background: string }; layers: any[] } } | null
  students: SectionCardStudent[]
}

export const getSectionReportCards = async (params: {
  breakdown: string
  class: string
  section: string
}): Promise<{ success: boolean; data: SectionReportCards }> => {
  const res = await apiClient({ method: "GET", url: appendQueryParams(`${BASE}/section`, params) })
  return res.data
}

// ── Report card (per student) ──────────────────────────────────────
export const getReportCardStudents = async (params: Record<string, any>) => {
  const res = await apiClient({ method: "GET", url: appendQueryParams(`${BASE}/students`, params) })
  return res.data
}

export const getReportCard = async (enrollmentId: string, breakdownId: string) => {
  const res = await apiClient({
    method: "GET",
    url: appendQueryParams(`${BASE}/${enrollmentId}`, { breakdownId }),
  })
  return res.data
}

// ── Report-card PDF + templates (designer) ─────────────────────────
export const renderReportCardPdf = async (
  enrollmentId: string,
  breakdownId: string,
  templateId?: string
): Promise<{ success: boolean; url: string; key: string }> => {
  const res = await apiClient({
    method: "GET",
    url: appendQueryParams(`${BASE}/${enrollmentId}/pdf`, { breakdownId, templateId }),
  })
  return res.data
}

export type ReportCardTemplate = {
  _id: string
  name: string
  layerCount?: number
  masterTemplate?: { _id: string; name: string; scopeLabel?: string } | null
  branch?: { _id: string; name: string; logoKey?: string; headerImageKey?: string } | null
  session?: { _id: string; title: string } | null
  term?: { _id: string; title: string } | null
  classes?: { _id: string; name: string; order?: number }[]
  schoolName?: string
  schoolTagline?: string
  logoKey?: string
  columns?: { showObtained?: boolean; showTotal?: boolean; showPercent?: boolean; showGrade?: boolean; showRemarks?: boolean }
  showGradeKey?: boolean
  showPosition?: boolean
  showPromotion?: boolean
  signatures?: string[]
  headerColor?: string
  accentColor?: string
  definition?: { layers: any[]; backgroundColor?: string } | null
  isActive?: boolean
  isDeleted?: boolean
  createdAt?: string
  updatedAt?: string
}

export type ReportCardMasterTemplate = {
  _id: string
  name: string
  scopeLabel?: string
  notes?: string
  layerCount?: number
  linkedTemplates?: number
  schoolName?: string
  schoolTagline?: string
  logoKey?: string
  columns?: { showObtained?: boolean; showTotal?: boolean; showPercent?: boolean; showGrade?: boolean; showRemarks?: boolean }
  showGradeKey?: boolean
  showPosition?: boolean
  showPromotion?: boolean
  signatures?: string[]
  headerColor?: string
  accentColor?: string
  isActive?: boolean
  createdAt?: string
  updatedAt?: string
}

// ── Master Templates ───────────────────────────────────────────────
export const masterTemplatesApi = {
  list: async (params?: Record<string, any>): Promise<{ success: boolean; data: ReportCardMasterTemplate[] }> => {
    const res = await apiClient({ method: "GET", url: appendQueryParams(`${BASE}/master-templates`, params || {}) })
    return res.data
  },
  get: async (id: string): Promise<{ success: boolean; data: ReportCardMasterTemplate }> => {
    const res = await apiClient({ method: "GET", url: `${BASE}/master-templates/${id}` })
    return res.data
  },
  create: async (data: Partial<ReportCardMasterTemplate>) => {
    const res = await apiClient({ method: "POST", url: `${BASE}/master-templates`, data })
    return res.data
  },
  update: async (id: string, data: Partial<ReportCardMasterTemplate>) => {
    const res = await apiClient({ method: "PUT", url: `${BASE}/master-templates/${id}`, data })
    return res.data
  },
  duplicate: async (id: string, name: string) => {
    const res = await apiClient({ method: "POST", url: `${BASE}/master-templates/${id}/duplicate`, data: { name } })
    return res.data
  },
  remove: async (id: string) => {
    const res = await apiClient({ method: "DELETE", url: `${BASE}/master-templates/${id}` })
    return res.data
  },
  restore: async (id: string) => {
    const res = await apiClient({ method: "POST", url: `${BASE}/master-templates/${id}/restore` })
    return res.data
  },
}

// ── Templates ──────────────────────────────────────────────────────
export const templatesApi = {
  list: async (params?: Record<string, any>): Promise<{ success: boolean; data: ReportCardTemplate[] }> => {
    const res = await apiClient({ method: "GET", url: appendQueryParams(`${BASE}/templates`, params || {}) })
    return res.data
  },
  get: async (id: string): Promise<{ success: boolean; data: ReportCardTemplate }> => {
    const res = await apiClient({ method: "GET", url: `${BASE}/templates/${id}` })
    return res.data
  },
  create: async (data: Partial<ReportCardTemplate>) => {
    const res = await apiClient({ method: "POST", url: `${BASE}/templates`, data })
    return res.data
  },
  update: async (id: string, data: Partial<ReportCardTemplate>) => {
    const res = await apiClient({ method: "PUT", url: `${BASE}/templates/${id}`, data })
    return res.data
  },
  duplicate: async (id: string, name: string) => {
    const res = await apiClient({ method: "POST", url: `${BASE}/templates/${id}/duplicate`, data: { name } })
    return res.data
  },
  clone: async (id: string, data: { name: string; session?: string; term?: string; classes?: string[]; branch?: string }) => {
    const res = await apiClient({ method: "POST", url: `${BASE}/templates/${id}/clone`, data })
    return res.data
  },
  remove: async (id: string) => {
    const res = await apiClient({ method: "DELETE", url: `${BASE}/templates/${id}` })
    return res.data
  },
  restore: async (id: string) => {
    const res = await apiClient({ method: "POST", url: `${BASE}/templates/${id}/restore` })
    return res.data
  },
}

// ── Designer asset upload (reuses the cert S3 bucket) ─────────────
// POST /certificates/assets/upload  { image: File }  →  { key, url }
export const uploadRcAssetApi = async (file: File): Promise<{ key: string; url: string }> => {
  const fd = new FormData()
  fd.append("image", file)
  const res = await apiClient({ method: "POST", url: "/certificates/assets/upload", data: fd })
  return res.data
}

// GET /certificates/asset-url?key=…  →  { url }
export const rcAssetUrlApi = async (key: string): Promise<{ url: string }> => {
  const res = await apiClient({ method: "GET", url: `/certificates/asset-url`, params: { key } })
  return res.data
}

// Asset library (designer left rail) — folders + images per folder.
export const listRcAssetFolders = async (): Promise<string[]> => {
  const res = await apiClient({ method: "GET", url: `/certificates/rc-asset-folders` })
  return res.data.data || []
}
export type RcAsset = { key: string; name: string; url: string }
export const listRcAssets = async (folder: string): Promise<RcAsset[]> => {
  const res = await apiClient({ method: "GET", url: `/certificates/rc-assets`, params: { folder } })
  return res.data.data || []
}

// POST /certificates/asset-urls { keys }  →  { key: url, ... }
// One round trip for every asset a page needs instead of one request per key
// (a template with a dozen+ image layers was issuing a dozen+ sequential
// presign requests — this is what made the designer/print sheet feel slow).
export const rcAssetUrlsApi = async (keys: string[]): Promise<Record<string, string>> => {
  const unique = Array.from(new Set(keys.filter(Boolean)))
  if (!unique.length) return {}
  const res = await apiClient({ method: "POST", url: `/certificates/asset-urls`, data: { keys: unique } })
  return res.data.data
}

// ── Legacy compatibility ───────────────────────────────────────────
export const listReportCardTemplates = templatesApi.list
export const createReportCardTemplate = templatesApi.create
export const updateReportCardTemplate = templatesApi.update
export const deleteReportCardTemplate = templatesApi.remove
